import { cancelOrder, createOrder, fraudOrder, getOrderEditByDetail, udpateOrder, updateOrderEditByDetail } from "api/order";
import { AppContext } from "Context/AppContext";
import { useUser } from "Context/UserContext/useUser";
import { useFormikContext } from "formik";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { initialValue } from "../context/order";
import { useOrderAttributeProvider } from "../context/order";
import { createOrderObject } from "../utility/createOrderObject";
import { editOrderObject } from "../utility/editOrderObject";
import * as Sentry from '@sentry/react';
import moment from "moment";
import { PAYMENT_METHODS } from "Helpers/Constants";
import { useCustomer } from "Context/Customer/useCustomer";
import { validateNotAllowStoreOrder } from "../utility/util";
import { buildSentryExtra } from "utils/sanitizeData";
export function useProcessingOrder() {
  const formik = useFormikContext();
  const { customerState } = useCustomer();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const { summary, currentCard, orderDetail, setModal, estimating } =
    useOrderAttributeProvider();
  const { userState, reLogin } = useUser();
  const { setIsLoading, showToastMessage, lastOrderEditPath } = useContext(AppContext);
  const navigate = useNavigate();

  const getUser = async () => {
    if (userState?.userProfile?.id) {
      return userState?.userProfile?.id;
    } else {
      const user = await reLogin();
      return user.id;
    }
  };

  const createOrderStart = async () => {
    let post_body;
    let payload;
    const user_id = await getUser();
    if (formik.values.payment_type === "credit_card") {
      try {
        //PAYABLI
        //Remove payment_method condition and set default payabli payment method for create nonce
        //Create Object for Payabli payment same as Braintree paybload nonce
        //Braintree was the previous payment gateway, now we are using Payabli payment gateway for all stores
        const payabliResponse = customerState.paymentNonce?.responseData;
        payload = {
          nonce: payabliResponse?.referenceId,
          methodReferenceId: payabliResponse?.methodReferenceId,
        };
        post_body = createOrderObject(
          formik.values,
          summary,
          user_id,
          currentCard,
          payload
        );
      } catch (error) {
        const sentryExtra = buildSentryExtra({
          error: error?.response?.data ?? { message: error?.message },
          body: post_body,
          functionName: "createOrderStart",
          message: error?.message,
          generateTime: new Date().toLocaleString(),
        });
        Sentry.captureMessage("ERROR OCCURED", { extra: sentryExtra, level: 'error' });
        setIsLoading(false);
        showToastMessage(
          undefined,
          `There is some issue with the payment information you entered. Please fill up the card details properly.`,
          "error",
          false
        );
        return;
      }
    } else {
      post_body = createOrderObject(
        formik.values,
        summary,
        user_id,
        currentCard,
        null
      );
    }
    // Guard: never POST an unresolved delivery date/time. The custom / "Anytime"
    // delivery path can yield Luxon's literal "Invalid DateTime" string, which the
    // API rejects with a cryptic "Invalid DateTime" message — leaving the CSR with a
    // Save that appears to do nothing. Block here with a clear, actionable message.
    const isInvalidDate = (v) => !v || v === "Invalid DateTime";
    const hasInvalidDeliveryDate = (post_body?.order ?? []).some(
      (o) => isInvalidDate(o?.delivery_date_time) || isInvalidDate(o?.delivery_date)
    );
    if (hasInvalidDeliveryDate) {
      setIsLoading(false);
      Sentry.captureMessage("Order submit blocked: invalid delivery date/time", {
        extra: buildSentryExtra({
          body: post_body,
          functionName: "createOrderStart",
          message: "Invalid delivery date/time blocked before POST",
          generateTime: new Date().toLocaleString(),
        }),
        level: "warning",
      });
      showToastMessage(
        undefined,
        "Please select a valid delivery date and time before saving this order.",
        "error",
        false
      );
      return;
    }
    Sentry.captureMessage(`Create Order started from CSR for customer ${post_body?.customer_info?.customer_first_name} ${post_body?.customer_info?.customer_last_name}`);
    try {
      await createOrder(post_body);
      setIsLoading(false);
      navigate(lastOrderEditPath);
      formik.resetForm();
      formik.setValues((v) => {
        return {
          ...initialValue,
        };
      });
      showToastMessage(
        undefined,
        `Order placed successfully`,
        "success",
        false
      );
    } catch (error) {
      setIsLoading(false);
      showToastMessage(
        undefined,
        `${error.response?.data?.message}`,
        "error",
        false
      );
    }
  };

  const processOrder = async () => {
    await formik.validateForm();
    if (!formik.isValid) {
      formik.submitForm();
      setShowErrorModal(true);
      return;
    } else {
      // validate to not allow place order for specific store.
      const isvalidateNotAllowStoreOrder = validateNotAllowStoreOrder(
        formik.values.billing_info.payment_method,
        formik.values?.payabli_paypoint,
        formik.values?.billing_info?.zip_code_store_id
      );
      if(isvalidateNotAllowStoreOrder){
        setIsLoading(false);
        showToastMessage(undefined, "Currently, we are not accepting online orders for the selected location. However, we will start soon.", "error", false);
        formik.setValues((v) => {
          return {
            ...v,
            delivery_information:{
              ...v.delivery_information,
              zipcode: ""
            },
          };
        });
        return;
      }
      // Check delivery time and product price for on-demand order
      if(formik.values?.delivery_information?.burq_quote_id  && formik.values?.delivery_information?.burq_quote_id !== "") {

        let currentDateTime = moment.utc().format();          // Get current date and time in utc
        let deliveryDateTime = moment.utc(formik.values.delivery_information.delivery_date_time).format();          // Get delivery date and time in utc

        // Calculate hours difference between current date time and delivery date time
        var deliveryHoursDuration = moment.duration(moment(deliveryDateTime).diff(moment(currentDateTime)));
        var deliveryHoursDiff = deliveryHoursDuration.asHours();

        // Get burq quote expires date and time in utc
        let burqQuoteExpires = (formik.values?.delivery_information?.burq_quote_expires && formik.values?.delivery_information?.burq_quote_expires !== "" && formik.values?.delivery_information?.burq_quote_expires !== null) ? moment.utc(formik.values.delivery_information.burq_quote_expires).format() : "";
        
        // Calculate current date time before burq quote expires date time
        var burqQuoteExpiresBefore = burqQuoteExpires ? moment(currentDateTime).isBefore(moment(burqQuoteExpires)) : true;
        // Calculate total price of products
        let totalProductPrice = 0;
        await formik?.values?.recipients.map(async (recipientsObj) => {
          await recipientsObj?.products.map((productObj) => {
            totalProductPrice += productObj.total_price
          })
        })

        if(formik.values.is_subscribe_order === 1 || (formik?.values?.recipients && formik?.values?.recipients.length > 1)){
          // Null delivery date and time information object
          formik.setValues((v) => ({
            ...v,
            delivery_information: {
              ...v.delivery_information,
              delivery_date: "",
              delivery_date_time: "",
              delivery_time: "",
              delivery_time_id: 0,
              delivery_time_type: "",
              burq_quote_id:"",
              burq_quote_expires:""
            }
          }));
          showToastMessage(undefined,"Please reselect the delivery date and time.","error",false);
        }else{
          // Check the time slot with delivery time with 2 hours different from the current time
          if(deliveryHoursDiff < 2 || totalProductPrice > 400 || !burqQuoteExpiresBefore) {
            // Null delivery date and time information object
            formik.setValues((v) => ({
              ...v,
              delivery_information: {
                ...v.delivery_information,
                delivery_date: "",
                delivery_date_time: "",
                delivery_time: "",
                delivery_time_id: 0,
                delivery_time_type: "",
                burq_quote_id:"",
                burq_quote_expires:""
              }
            }));
            showToastMessage(
              undefined,
              (deliveryHoursDiff < 2 || !burqQuoteExpiresBefore) ? "Please reselect the delivery date and time." : summary?.flower_total_amount > 400 ? "Please reselect the delivery date & time. Your cart size requires special care by our team." : "" ,
              "error",
              false
            );
          }
          else {
            setIsLoading(true);
            createOrderStart();
          }
        }
      }else {
        setIsLoading(true);
        createOrderStart();
      }
    }
  };

  const makeOrderFraud = async (note) => {
    setIsLoading(true);
    let post_req = {
      order_id: orderDetail.id,
      notes: note,
    };
    try {
      let response = await fraudOrder(post_req);
      setModal("");
      setIsLoading(false);
      showToastMessage(undefined, response.data.message, "success", false);
      navigate(lastOrderEditPath);
    } catch (error) {
      setModal("");
      setIsLoading(false);
      showToastMessage(undefined, error.response.data.message, "error", false);
    }
  };

  const cancelOrderCall = async (cancelOrderData) => {
    // cancelOrderData is an object that contains the following properties:
    // order_id: the id of the order to be cancelled
    // refund: the amount to be refunded
    // fullRefundFlag: a boolean flag indicating whether the refund is a full refund
    // refund_flag: the flag indicating the type of refund
    // reason_message_option: the option selected for the reason message
    // notes: the notes for the refund
    try {
      setIsLoading(true);
      let response = await cancelOrder(cancelOrderData);
      setModal("");
      setIsLoading(false);
      showToastMessage(undefined, response.data.message, response.data.data?.refund_queued ? "warning" : "success", false);
      navigate(lastOrderEditPath);
    } catch (error) {
      showToastMessage(undefined, error.response.data.message, "error", false);
      setModal("");
      setIsLoading(false);
    }
  };

  const createEditOrderObject = async (action, note) => {
    // Wrike 4594810284: a tax estimate in flight belongs to a newer charge; posting now saves the old tax.
    if (estimating) {
      showToastMessage(undefined, "The tax is still being calculated. Please save again in a moment.", "error", false);
      return;
    }
    // validate to not allow place order for specific store.
    const isvalidateNotAllowStoreOrder = validateNotAllowStoreOrder(
      formik.values.billing_info.payment_method,
      formik.values?.payabli_paypoint,
      formik.values?.billing_info?.zip_code_store_id
    );

    if(isvalidateNotAllowStoreOrder){
      setIsLoading(false);
      showToastMessage(undefined, "Currently, we are not accepting online orders for the selected location. However, we will start soon.", "error", false);
      formik.setValues((v) => {
        return {
          ...v,
          delivery_information:{
            ...v.delivery_information,
            zipcode: ""
          },
        };
      });
      return;
    }

    setIsLoading(true);
    const user_id = await getUser();
    const post_body = editOrderObject(
      formik.values,
      summary,
      user_id,
      orderDetail,
      action,
      note
    );
    // Same guard as createOrderStart — an unresolved delivery date/time serializes
    // to "Invalid DateTime" and the update is rejected with no clear feedback.
    const isInvalidEditDate = (v) => !v || v === "Invalid DateTime";
    const editHasInvalidDeliveryDate = (post_body?.order ?? []).some(
      (o) => isInvalidEditDate(o?.delivery_date_time) || isInvalidEditDate(o?.delivery_date)
    );
    if (editHasInvalidDeliveryDate) {
      setIsLoading(false);
      Sentry.captureMessage("Order update blocked: invalid delivery date/time", {
        extra: buildSentryExtra({
          body: post_body,
          functionName: "createEditOrderObject",
          message: "Invalid delivery date/time blocked before update POST",
          generateTime: new Date().toLocaleString(),
        }),
        level: "warning",
      });
      showToastMessage(
        undefined,
        "Please select a valid delivery date and time before saving this order.",
        "error",
        false
      );
      return;
    }
    Sentry.captureMessage(`Edit Order started from CSR for customer ${post_body?.customer_info?.customer_first_name} ${post_body?.customer_info?.customer_last_name}`);
    try {
      const response = await udpateOrder(post_body);
      setModal("");
      setIsLoading(false);
      showToastMessage(undefined, response.data.message, response.data.data?.refund_queued ? "warning" : "success", false);
      navigate(lastOrderEditPath);
    } catch (error) {
      showToastMessage(undefined, error.response.data.message, "error", false);
      setModal("");
      setIsLoading(false);
    }
  };

  const approveOrder = async (note) => {
    await formik.validateForm();
    if (!formik.isValid) {
      formik.submitForm();
      setShowErrorModal(true);
      return;
    } else {
      createEditOrderObject("submit", note);
    }
  };

  const saveOrder = async (note) => {
    await formik.validateForm();
    if (!formik.isValid) {
      formik.submitForm();
      setShowErrorModal(true);
      return;
    } else {
      createEditOrderObject("save", note);
    }
  };

  const validate = async () => {
    await formik.validateForm();
    if (!formik.isValid) {
      formik.submitForm();
      setShowErrorModal(true);
      return true;
    } else {
      return false;
    }
  };

  const orderAssignCheck = async (editButton = false) => {

    let postData = {
      orderId: orderDetail.id,
      editButton: editButton
    }
    const response = await getOrderEditByDetail(postData);
    return response.data;
  };

  const overRideEditOrder = async () => {
    let postData = {
      orderId: orderDetail.id
    }

    const response = await updateOrderEditByDetail(postData);

    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  };

  const cancleOverRideEditOrder = async () => {
    navigate(lastOrderEditPath);
  };

  return {
    showErrorModal,
    processOrder,
    setShowErrorModal,
    makeOrderFraud,
    cancelOrderCall,
    approveOrder,
    saveOrder,
    validate,
    orderAssignCheck,
    overRideEditOrder,
    cancleOverRideEditOrder
  };
}
